import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { LayoutDashboard, Calculator, FileText, Receipt, FolderKanban, TrendingUp, Settings, LogOut, Shield, Users, Bell, Clock, Wrench, DollarSign, ShoppingCart, Factory, Bug, X, Send, BarChart3, CalendarDays, CreditCard, FileDown, Menu } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Layout = ({ user, onLogout }) => {
  const location = useLocation();
  const [urgentCount, setUrgentCount] = useState(0);
  const [bugStats, setBugStats] = useState({ open_count: 0, critical_count: 0 });
  const [bugModalOpen, setBugModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bugForm, setBugForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    page_url: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const navigation = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard },
    { name: 'WorkBoard', path: '/workboard', icon: Clock },
    { name: 'CRM', path: '/crm', icon: Users },
    { name: 'Estimates', path: '/estimates', icon: FileText },
    { name: 'Quote Manager', path: '/quote-manager', icon: FileText },
    { name: 'Projects', path: '/projects', icon: FolderKanban },
    { name: 'Production Manager', path: '/production-manager', icon: Factory },
    { name: 'Tools', path: '/tools', icon: Wrench },
  ];

  const financeNavigation = [
    { name: 'Invoices', path: '/invoices', icon: Receipt },
    { name: 'Billing', path: '/billing', icon: CreditCard },
    { name: 'Purchase Orders', path: '/purchase-orders', icon: ShoppingCart },
    { name: 'Costing Setup', path: '/costing', icon: Calculator },
    { name: 'Burn Rate Manager', path: '/burn-rate', icon: DollarSign },
    { name: 'Labour Tracking', path: '/labour-tracking', icon: Clock },
    { name: 'Performance', path: '/performance', icon: TrendingUp },
    { name: 'Analytics', path: '/analytics', icon: BarChart3 },
    { name: 'Scheduling', path: '/scheduling', icon: CalendarDays },
    { name: 'Reports', path: '/reports', icon: FileDown },
  ];

  const settingsNavigation = [
    { name: 'Company Settings', path: '/settings', icon: Settings },
    { name: 'Work Calendar', path: '/work-calendar', icon: Clock },
    { name: 'AI Assistant', path: '/settings/ai', icon: Settings },
    { name: 'Security', path: '/security', icon: Shield },
    { name: 'Bug Reports', path: '/bug-reports', icon: Bug, adminOnly: true },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Load pending message count
  useEffect(() => {
    loadMessageCount();
    loadBugStats();
    
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      loadMessageCount();
      loadBugStats();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadMessageCount = async () => {
    try {
      const response = await axios.get(`${API}/messages/pending-actions`);
      setUrgentCount(response.data.high_priority_count || 0);
    } catch (error) {
      // Silently fail - don't show errors for background polling
      console.error('Failed to load message count:', error);
    }
  };

  const loadBugStats = async () => {
    try {
      const response = await axios.get(`${API}/bugs/stats`);
      setBugStats(response.data);
    } catch (error) {
      console.error('Failed to load bug stats:', error);
    }
  };

  const handleOpenBugModal = () => {
    setBugForm({
      title: '',
      description: '',
      priority: 'medium',
      page_url: window.location.href
    });
    setBugModalOpen(true);
  };

  const handleSubmitBug = async () => {
    if (!bugForm.title.trim() || !bugForm.description.trim()) {
      toast.error('Please fill in title and description');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/bugs`, {
        ...bugForm,
        browser_info: navigator.userAgent
      });
      toast.success('Bug report submitted successfully!');
      setBugModalOpen(false);
      loadBugStats();
    } catch (error) {
      toast.error('Failed to submit bug report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-screen bg-background" data-testid="app-layout">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-card border border-border rounded-sm"
        data-testid="mobile-menu-toggle"
      >
        <Menu size={24} />
      </button>

      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed lg:relative inset-y-0 left-0 z-40
          w-64 bg-card border-r border-border flex flex-col
          transform transition-transform duration-200 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <h1 className="text-2xl font-black tracking-tight uppercase text-primary" data-testid="app-logo">
            PSM WELDING
          </h1>
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mt-1">
            INDUSTRIAL OS
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="navigation">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const isDashboard = item.name === 'Dashboard';
            const showBadge = isDashboard && urgentCount > 0;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                className={`
                  flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium uppercase tracking-wide
                  transition-colors duration-200 relative
                  ${
                    active
                      ? 'bg-primary/10 text-primary border-l-2 border-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }
                `}
              >
                <Icon size={18} />
                <span>{item.name}</span>
                {showBadge && (
                  <span className="ml-auto flex items-center gap-1 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                    <Bell size={12} />
                    {urgentCount}
                  </span>
                )}
              </Link>
            );
          })}

          {/* Finance Section */}
          <div className="pt-4 mt-4 border-t border-border">
            <p className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">
              Finance
            </p>
            {financeNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  className={`
                    flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium uppercase tracking-wide
                    transition-colors duration-200
                    ${
                      active
                        ? 'bg-primary/10 text-primary border-l-2 border-primary'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    }
                  `}
                >
                  <Icon size={18} />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>

          {/* Settings Section */}
          <div className="pt-4 mt-4 border-t border-border">
            <p className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">
              Settings
            </p>
            {settingsNavigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              // Hide admin-only items from non-admin users
              if (item.adminOnly && !['Admin', 'Owner', 'Manager'].includes(user.role)) {
                return null;
              }
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  className={`
                    flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium uppercase tracking-wide
                    transition-colors duration-200
                    ${
                      active
                        ? 'bg-primary/10 text-primary border-l-2 border-primary'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    }
                  `}
                >
                  <Icon size={18} />
                  <span>{item.name}</span>
                  {item.name === 'Bug Reports' && bugStats.open_count > 0 && (
                    <span className="ml-auto px-2 py-0.5 bg-orange-500 text-white text-xs font-bold rounded-full">
                      {bugStats.open_count}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-border" data-testid="user-section">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-sm bg-primary/20 flex items-center justify-center">
              <span className="text-primary font-bold text-sm font-mono">
                {user.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <div className="text-sm font-medium">{user.name}</div>
              <div className="text-xs text-muted-foreground font-mono uppercase">{user.role}</div>
            </div>
          </div>
          <button
            onClick={handleOpenBugModal}
            data-testid="report-bug-button"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-2 bg-orange-500/10 text-orange-600 rounded-sm text-sm font-mono uppercase tracking-wider hover:bg-orange-500/20 transition-colors"
          >
            <Bug size={16} />
            Report Bug
          </button>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-destructive/10 text-destructive rounded-sm text-sm font-mono uppercase tracking-wider hover:bg-destructive/20 transition-colors"
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto relative z-10 lg:ml-0 pt-16 lg:pt-0" data-testid="main-content">
        <Outlet />
      </main>

      {/* Bug Report Modal */}
      <Dialog open={bugModalOpen} onOpenChange={setBugModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-orange-500" />
              Report a Bug
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground">Title *</label>
              <Input
                value={bugForm.title}
                onChange={(e) => setBugForm({...bugForm, title: e.target.value})}
                placeholder="Brief description of the issue"
                className="mt-1"
                data-testid="bug-title-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Description *</label>
              <Textarea
                value={bugForm.description}
                onChange={(e) => setBugForm({...bugForm, description: e.target.value})}
                placeholder="Detailed steps to reproduce the bug..."
                className="mt-1"
                rows={4}
                data-testid="bug-description-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Priority</label>
              <Select 
                value={bugForm.priority} 
                onValueChange={(val) => setBugForm({...bugForm, priority: val})}
              >
                <SelectTrigger className="mt-1" data-testid="bug-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground">Page URL</label>
              <Input
                value={bugForm.page_url}
                onChange={(e) => setBugForm({...bugForm, page_url: e.target.value})}
                placeholder="Current page URL"
                className="mt-1"
                data-testid="bug-url-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBugModalOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSubmitBug} 
              disabled={submitting}
              data-testid="submit-bug-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Report'}
              <Send className="ml-2 h-4 w-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Layout;
